/**
 * Direct manipulation in edit mode (pure, unit tested): what a pointerdown on a page starts and what
 * a tap does with each tool. The input policy (who may act: Pencil, finger, mouse; palms) is in
 * gesture.ts.
 *
 *  - Penni, Yfirstrikun, Texti and Tákn select own TEXTS and SYMBOLS with a tap. A tap never selects
 *    ink or a highlight, so a handwritten dot, an accent or a breath mark drawn on own ink or on a
 *    highlight is never swallowed by a selection, and a symbol or a text can still be put on a
 *    highlighted line. They pick with a small tolerance, so symbols can be placed close together.
 *  - A LONG PRESS with those tools (LONG_PRESS_MS held still: gesture.ts longPressSlopPx) selects any
 *    own mark but a connector — ink and highlights too, but only ON the mark (LONG_PRESS_TOLERANCE_PX),
 *    not beside it — and the same pointer then drags it; the stroke, dot or tap it began as is
 *    dropped. Nothing own under it, or the pointer drifted (a slowly drawn dot): it goes on.
 *  - Texti: a tap on the selected text (or "Breyta", or a double tap) opens the editor.
 *  - With a selection, a pointerdown on its frame or handles moves / resizes it; anywhere else the
 *    tool acts as usual and the selection ends. A tap that does not travel on the move area or a
 *    handle outside the frame (the invisible part of the ≥ 44 pt targets) counts as a tap next to
 *    the selection; only a tap on a handle's visible icon leaves everything as it is. Ink and
 *    highlights selected in Penni / Yfirstrikun are grabbed only on the mark itself and by their
 *    grip and resize handle (selectionGrabbedOnMark): elsewhere in their frame the tool writes.
 *  - A finger on a Pencil-only device never selects with Penni, Yfirstrikun, Texti or Tákn
 *    (gesture.ts tapMaySelect): in Texti / Tákn its tap works as before this feature.
 *  - Velja keeps its behaviour: any own object incl. connectors; a pen/mouse picks and drags at once.
 *  - Tengill selects own connectors (pins); Strokleður never selects.
 */
import type { AnnotationKind, AnnotationObject } from '../../shared/annotations';
import { cssToUnits, hitObject, HIT_TOLERANCE_PX, objectBounds, rectContains, SELECTION_PAD_PX, type HitContext, type Pt } from './geometry';
import { classifyPointerDown, GRAB_TOOLS, tapMaySelect, type PointerDownInput } from './gesture';
import type { ToolId } from './tools';

/** Hit tolerance (CSS px) of a tap that selects with Penni, Yfirstrikun, Texti and Tákn. */
export const TAP_SELECT_TOLERANCE_PX = 6;

/** Can a tap with `tool` select an own object of this kind? */
export function tapSelects(tool: ToolId, kind: AnnotationKind): boolean {
  switch (tool) {
    case 'select':
      return true;
    case 'connector':
      return kind === 'connector';
    case 'eraser':
      return false;
    default:
      return kind === 'text' || kind === 'stamp';
  }
}

/** Hit tolerance (CSS px) of a selecting tap with `tool`. */
export function tapTolerancePx(tool: ToolId): number {
  return tool === 'select' || tool === 'connector' ? HIT_TOLERANCE_PX : TAP_SELECT_TOLERANCE_PX;
}

/** Part of the current selection under the pointer: its corner handle, or its frame / move area. */
export type SelectionPart = 'move' | 'scale';

export interface DownInput extends Omit<PointerDownInput, 'onSelection'> {
  /** null: not on the selection (or nothing is selected). */
  on: SelectionPart | null;
  /** The selection can be resized with this tool (selectionScalable). */
  scalable: boolean;
}

/**
 * Does the selection get its resize handle (and move grip) with this tool? Texts, symbols, ink and
 * highlights always; a connector (its pin's size, about the pin's centre) with a tool that drags the
 * selection (Velja) — with Tengill pins are only tapped, and A− / A+ resize them.
 */
export function selectionScalable(kind: AnnotationKind, tool: ToolId): boolean {
  return kind !== 'connector' || GRAB_TOOLS.includes(tool);
}

/**
 *  'ignore'    – palm, hovering pen, other buttons, a navigating finger;
 *  'tap'       – decide on pointerup with resolveTap() if the pointer did not travel;
 *  'move'      – drag the selection;
 *  'scale'     – resize the selection from its corner;
 *  'pick-move' – Velja with a pen/mouse: select the own object under the pointer and drag it;
 *  'stroke'    – the tool's own drag (ink, highlighter, eraser); ends any selection, and a pen or
 *                highlighter stroke that stays a tap is resolved with resolveTap() too.
 */
export type DownRoute = 'ignore' | 'tap' | 'move' | 'scale' | 'pick-move' | 'stroke';

export function routePointerDown(i: DownInput): DownRoute {
  const a = classifyPointerDown({ ...i, onSelection: i.on !== null });
  if (a !== 'drag') return a;
  if (i.on !== null && GRAB_TOOLS.includes(i.tool)) return i.on === 'scale' && i.scalable ? 'scale' : 'move';
  return i.tool === 'select' ? 'pick-move' : 'stroke';
}

/** Tools with which a long press selects an own mark (Velja picks at once; Tengill, Strokleður never). */
export const LONG_PRESS_TOOLS: readonly ToolId[] = ['pen', 'highlighter', 'text', 'stamp'];

/**
 * Hit tolerance (CSS px) of a long press: a tap's (TAP_SELECT_TOLERANCE_PX), so a pause next to own
 * ink — the start of the next letter — never grabs it; a highlight is still hit across its band.
 */
export const LONG_PRESS_TOLERANCE_PX = TAP_SELECT_TOLERANCE_PX;

/** Can a long press select an own object of this kind? All but connectors (Tengill / Velja select pins). */
export function longPressSelects(kind: AnnotationKind): boolean {
  return kind !== 'connector';
}

/**
 * Is the selection grabbed only on the mark itself (LONG_PRESS_TOLERANCE_PX) and by its move grip and
 * resize handle, not across its whole frame? Ink and highlights in Penni / Yfirstrikun (selected by a
 * long press): a slur or a highlighted lyric line spans a bar, and a fingering or a dot written inside
 * its frame must write, not drag it. Elsewhere in the frame the tool acts as next to any selection: a
 * stroke that travels or is kept ends it, a tap only ends it. Texts, symbols and pins, and every
 * selection in Velja, Texti and Tákn, keep the whole frame (and the ≥ 44 pt move area).
 */
export function selectionGrabbedOnMark(kind: AnnotationKind, tool: ToolId): boolean {
  return (kind === 'ink' || kind === 'highlight') && (tool === 'pen' || tool === 'highlighter');
}

/**
 * Is page point `p` (page units) on the selection `o` with `tool`: on the mark itself where
 * selectionGrabbedOnMark, else inside its (padded) frame? `w` = the page box's CSS width.
 */
export function onSelectionArea(o: AnnotationObject, p: Pt, tool: ToolId, ctx: HitContext, w: number): boolean {
  if (selectionGrabbedOnMark(o.kind, tool)) return hitObject(o, p, cssToUnits(LONG_PRESS_TOLERANCE_PX, w), ctx);
  return rectContains(objectBounds(o, ctx), p, cssToUnits(SELECTION_PAD_PX, w));
}

/**
 * Does this pointerdown arm the long-press timer? Penni / Yfirstrikun (their stroke, which may still
 * turn out a dot) and Texti / Tákn (their tap), by a pointer that may select (gesture.ts
 * tapMaySelect: a Pencil or mouse, a finger only where fingers draw); never while a connector waits
 * for its target. Palms, a hovering Pencil and a second finger never get here (route 'ignore' / the
 * pan), and a press on the selection already moves it.
 */
export function armsLongPress(route: DownRoute, i: Pick<DownInput, 'pointerType' | 'tool' | 'fingersDraw' | 'choosingTarget'>): boolean {
  if (route !== 'stroke' && route !== 'tap') return false;
  if (i.choosingTarget || !LONG_PRESS_TOOLS.includes(i.tool)) return false;
  return tapMaySelect(i.pointerType, i.tool, i.fingersDraw);
}

export interface LongPressInput {
  /** Tool when the press went down, and now: a tool change ends the press. */
  armedTool: ToolId;
  tool: ToolId;
  /** The pointer travelled beyond the tap slop (a drift beyond longPressSlopPx already cleared the timer). */
  moved: boolean;
  /** A two-finger pan runs. */
  panning: boolean;
  /** The text editor is open. */
  textOpen: boolean;
  choosingTarget: boolean;
  /** Top-most own object under the pointer (LONG_PRESS_TOLERANCE_PX, longPressSelects), or null. */
  hit: { id: string; kind: AnnotationKind } | null;
}

/**
 * When the long-press timer fires: 'grab' – drop the pointer's wet stroke / dot / pending tap (nothing
 * is saved), select `hit` and let the same pointer drag it; 'none' – the stroke or tap goes on.
 */
export function resolveLongPress(i: LongPressInput): 'grab' | 'none' {
  if (i.moved || i.panning || i.textOpen || i.choosingTarget || i.tool !== i.armedTool) return 'none';
  return i.hit && longPressSelects(i.hit.kind) ? 'grab' : 'none';
}

/**
 *  'select'          – select the tapped own object;
 *  'edit-text'       – open the text editor on the (selected) text;
 *  'keep'            – the tap was on the selection: it stays, nothing else happens;
 *  'create-text'     – end the selection, open a new text box at the tap;
 *  'place-stamp'     – end the selection, place the current symbol at the tap (it becomes the selection);
 *  'choose-symbol'   – no symbol chosen yet: end the selection, open the palette;
 *  'start-connector' – end the selection, start a connector at the tap;
 *  'deselect'        – only end the selection;
 *  'draw'            – Penni / Yfirstrikun with nothing selected: keep what the stroke drew (a dot).
 */
export type TapAction = 'select' | 'edit-text' | 'keep' | 'create-text' | 'place-stamp' | 'choose-symbol' | 'start-connector' | 'deselect' | 'draw';

export interface TapInput {
  tool: ToolId;
  /**
   * The own object the tap is on: the top-most one this tool selects (tapSelects, within
   * tapTolerancePx), else the selection when the tap is inside its frame; null for empty space.
   */
  hit: { id: string; kind: AnnotationKind } | null;
  /** The selection when the pointer went down. */
  selectedId: string | null;
  /** The pointer went down on the selection's grab area (frame or handles), not on the page. */
  onHandle: boolean;
  /** Tákn: a symbol has been chosen. */
  hasGlyph: boolean;
  /** This pointer may select (gesture.ts tapMaySelect); false: a finger on a Pencil-only device. */
  selects: boolean;
}

export function resolveTap(i: TapInput): TapAction {
  const { tool, hit } = i;
  if (hit && hit.id === i.selectedId) return tool === 'text' && hit.kind === 'text' ? 'edit-text' : 'keep';
  if (hit && i.selects) return 'select';
  // A finger that may not select (Pencil-only device): Texti opens an own text, as it always did.
  if (hit && tool === 'text' && hit.kind === 'text') return 'edit-text';
  switch (tool) {
    case 'text':
      return 'create-text';
    case 'stamp':
      return i.hasGlyph ? 'place-stamp' : 'choose-symbol';
    case 'connector':
      return 'start-connector';
    case 'select':
      // Velja: a tap in the grab area around a small selection keeps it (as before).
      return i.onHandle ? 'keep' : 'deselect';
    case 'pen':
    case 'highlighter':
      return i.selectedId ? 'deselect' : 'draw';
    case 'eraser':
      return 'draw';
  }
}
